<template>
    <MainContent :title="pageTitle">
        <template #header>
            <Card customClasses="tw:mb-4">
                <p v-if="meta?.generated_at" class="tw:text-sm tw:text-gray-500 tw:mb-3">
                    {{ $t('back.dashboard.generated_at', { date: formatDate(meta.generated_at) }) }}
                </p>

                <!-- Season Selector -->
                <div v-if="loadingSeasons" class="tw:flex tw:items-center tw:gap-2">
                    <svg class="tw:animate-spin tw:h-5 tw:w-5 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.loading_seasons') }}</span>
                </div>

                <div v-else-if="error && !seasons.length" class="tw:flex tw:items-center tw:gap-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4">
                    <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
                    <Button variant="danger" @click="fetchSeasons">
                        {{ $t('back.dashboard.retry') }}
                    </Button>
                </div>

                <div v-else-if="!seasons.length" class="tw:bg-yellow-50 tw:border tw:border-yellow-200 tw:rounded-lg tw:p-4">
                    <p class="tw:text-sm tw:text-yellow-700">{{ $t('back.dashboard.empty_seasons') }}</p>
                </div>

                <div v-else class="tw:flex tw:items-center tw:gap-3">
                    <label class="tw:text-sm tw:font-medium tw:text-gray-700">
                        {{ $t('back.dashboard.season_label') }}
                    </label>
                    <BaseMultiselect
                        :options="seasonOptions"
                        :value="selectedSeasonIds"
                        labelKey="label"
                        valueKey="value"
                        :multiple="true"
                        :loading="loadingSeasons"
                        placeholder="Seleccionar temporadas"
                        @input="selectSeasons"
                    />
                </div>
            </Card>
        </template>

        <!-- TabsPanel with Overview + Economic + Evolution -->
        <TabsPanel :tabs="tabs">
            <template #tab-overview>
                <div class="tw:px-6 tw:py-6">
                    <!-- Error state for overview -->
                    <div v-if="error && seasons.length && !loadingOverview" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
                        <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
                        <Button variant="danger" @click="fetchOverview(selectedSeasonIds)">
                            {{ $t('back.dashboard.retry') }}
                        </Button>
                    </div>

                    <div class="tw:space-y-8">
                        <!-- Visión General -->
                        <div>
                            <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1">
                                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900">{{ $t('back.dashboard.overview_title') }}</h3>
                                <Badge v-if="isMultiSeason" variant="info" :label="$t('back.dashboard.comparison')" />
                            </div>
                            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.overview_description') }}</p>

                            <div class="tw:grid tw:grid-cols-2 lg:tw:grid-cols-4 tw:gap-4 tw:max-w-4xl tw:mx-auto">
                                <template v-if="isMultiSeason && comparisonTableData.length >= 2">
                                    <Card
                                        v-for="m in comparisonMetrics" :key="m.key"
                                        :title="m.label"
                                        customClasses="tw:border-l-2 tw:border-l-gray-200"
                                    >
                                        <div class="tw:space-y-2">
                                            <div
                                                v-for="v in m.values" :key="v.season"
                                                class="tw:flex tw:justify-between tw:items-center"
                                            >
                                                <span class="tw:text-xs tw:text-gray-400">{{ v.season }}</span>
                                                <span class="tw:text-sm tw:font-semibold tw:text-gray-800">{{ m.format(v.value) }}</span>
                                            </div>
                                            <div
                                                v-if="m.deltaLabel"
                                                class="tw:flex tw:justify-between tw:items-center tw:pt-2 tw:border-t tw:border-gray-100"
                                            >
                                                <span class="tw:text-xs tw:text-gray-500">Diferencia</span>
                                                <span :class="m.deltaClass">{{ m.deltaLabel }}</span>
                                            </div>
                                        </div>
                                    </Card>
                                </template>
                                <template v-else>
                                    <StatsCard
                                        variant="success"
                                        :title="$t('back.dashboard.total_revenue')"
                                        :value="hasKpiData ? formatCurrency(kpis?.revenue) : $t('back.dashboard.no_data')"
                                        :loading="loadingOverview"
                                    />
                                    <StatsCard
                                        variant="primary"
                                        :title="$t('back.dashboard.attendance_avg')"
                                        :value="hasKpiData ? formatNumber(Math.round(kpis?.attendance)) : $t('back.dashboard.no_data')"
                                        :loading="loadingOverview"
                                    />
                                    <StatsCard
                                        variant="warning"
                                        :title="$t('back.dashboard.abonados_count')"
                                        :value="hasKpiData ? formatNumber(kpis?.abonados) : $t('back.dashboard.no_data')"
                                        :loading="loadingOverview"
                                    />
                                    <StatsCard
                                        variant="danger"
                                        :title="$t('back.dashboard.occupancy_kpi')"
                                        :value="hasKpiData ? formatPercent(kpis?.occupancy) : $t('back.dashboard.no_data')"
                                        :loading="loadingOverview"
                                    />
                                </template>
                            </div>
                        </div>

                        <!-- Desglose de Ingresos -->
                        <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.revenue_title') }}</h3>
                            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.revenue_description') }}</p>

                            <div class="tw:flex tw:justify-center">
                                <div class="tw:w-full" :class="revenueChartType === 'ColumnChart' ? 'tw:max-w-4xl' : 'tw:max-w-2xl'">
                                    <Chart
                                        :type="revenueChartType || 'PieChart'"
                                        :data="revenueChartData"
                                        :options="revenueChartOptions"
                                        :loading="loadingOverview"
                                        :height="360"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Ocupación del Recinto -->
                        <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.occupancy_title') }}</h3>
                            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.occupancy_description') }}</p>

                            <div class="tw:flex tw:justify-center">
                                <div class="tw:w-full tw:max-w-md">
                                    <Chart
                                        :type="occupancyChartType || 'ColumnChart'"
                                        :data="occupancyChartData"
                                        :options="occupancyChartOptions"
                                        :loading="loadingOverview"
                                        :height="360"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Comparativa por Temporada -->
                        <div>
                            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.comparison_title') }}</h3>
                            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.comparison_description') }}</p>

                            <MetricTable
                                :title="''"
                                :columns="comparisonColumns"
                                :rows="comparisonTableData"
                                :emptyMessage="$t('back.dashboard.min_seasons_hint')"
                                :minRows="2"
                            />
                        </div>

                        <!-- Footer meta -->
                        <div v-if="meta?.generated_at" class="tw:text-center tw:text-xs tw:text-gray-400 tw:pt-4 tw:border-t tw:border-gray-100">
                            {{ $t('back.dashboard.generated_at', { date: formatDate(meta.generated_at) }) }}
                        </div>
                    </div>
                </div>
            </template>

            <template #tab-economic>
                <EconomicTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-evolution>
                <EvolutionTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-occupancy>
                <OccupancyTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-sales>
                <SalesTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-fans>
                <FansTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-matches>
                <MatchesTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>

            <template #tab-standings>
                <StandingsTab />
            </template>

            <template #tab-activity>
                <ActivityTab
                    :seasonIds="selectedSeasonIds"
                    :seasons="seasons"
                />
            </template>
        </TabsPanel>
    </MainContent>
</template>

<script setup>
    import { computed, onMounted } from 'vue';
    import { useDashboard } from '@/composables/admin/dashboard/useDashboard';
    import { formatCurrency, formatNumber, formatPercent, formatDate } from '@/utils/format';
    import { t } from '@/utils/i18n-helper';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import MetricTable from '@/modules/admin/ui/MetricTable.vue';
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Badge from '@/modules/admin/ui/Badge.vue';
    import EconomicTab from '@/modules/admin/dashboard/views/EconomicTab.vue';
    import EvolutionTab from '@/modules/admin/dashboard/views/EvolutionTab.vue';
    import OccupancyTab from '@/modules/admin/dashboard/views/OccupancyTab.vue';
    import SalesTab from '@/modules/admin/dashboard/views/SalesTab.vue';
    import FansTab from '@/modules/admin/dashboard/views/FansTab.vue';
    import MatchesTab from '@/modules/admin/dashboard/views/MatchesTab.vue';
    import StandingsTab from '@/modules/admin/dashboard/views/StandingsTab.vue';
    import ActivityTab from '@/modules/admin/dashboard/views/ActivityTab.vue';

    const {
        seasons,
        selectedSeasonIds,
        loadingSeasons,
        loadingOverview,
        error,
        currentSeasonData,
        isMultiSeason,
        kpis,
        revenueChartType,
        occupancyChartType,
        revenueChartData,
        occupancyChartData,
        comparisonTableData,
        meta,
        fetchSeasons,
        fetchOverview,
        selectSeasons,
    } = useDashboard();



    const pageTitle = computed(() =>
        t('back.dashboard.title') + (meta.value?.teatro_nombre ? ' — ' + meta.value.teatro_nombre : '')
    );

    const seasonOptions = computed(() =>
        seasons.value.map(s => ({ label: s.nombre, value: s.id }))
    );

    const hasKpiData = computed(() => kpis.value !== null);

    /** Métricas comparativas cuando hay 2+ temporadas seleccionadas */
    const comparisonMetrics = computed(() => {
        const data = comparisonTableData.value;
        if (!data || data.length < 2) return [];

        const defs = [
            { key: 'revenue', label: t('back.dashboard.total_revenue'), format: formatCurrency },
            { key: 'attendance', label: t('back.dashboard.attendance_avg'), format: v => formatNumber(Math.round(v)) },
            { key: 'abonados', label: t('back.dashboard.abonados_count'), format: formatNumber },
            { key: 'occupancy', label: t('back.dashboard.occupancy_kpi'), format: formatPercent },
        ];

        return defs.map(({ key, label, format }) => {
            const values = data.map(s => ({
                season: s.season,
                value: s[key],
            }));

            let deltaLabel = null;
            let deltaClass = 'tw:text-gray-500';

            // Delta solo cuando son exactamente 2 temporadas
            if (values.length === 2) {
                const numV1 = Number(values[0].value ?? 0);
                const numV2 = Number(values[1].value ?? 0);
                let deltaNumeric;

                if (key === 'occupancy') {
                    deltaNumeric = numV1 - numV2;
                    deltaLabel = (deltaNumeric >= 0 ? '+' : '') + deltaNumeric.toFixed(1) + ' %';
                } else {
                    const pct = numV2 !== 0 ? ((numV1 - numV2) / numV2) * 100 : 0;
                    deltaNumeric = pct;
                    deltaLabel = (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
                }

                deltaClass = deltaNumeric === 0
                    ? 'tw:text-gray-500'
                    : deltaNumeric > 0
                        ? 'tw:text-green-600 tw:font-semibold'
                        : 'tw:text-red-600 tw:font-semibold';
            }

            return { key, label, values, deltaLabel, deltaClass, format };
        });
    });

    const tabs = computed(() => [
        { key: 'overview', label: t('back.dashboard.overview_title') },
        { key: 'economic', label: t('back.dashboard.economic.tab') },
        { key: 'evolution', label: t('back.dashboard.evolution.tab') },
        { key: 'occupancy', label: t('back.dashboard.occupancy.tab') },
        { key: 'sales', label: t('back.dashboard.sales.tab') },
        { key: 'fans', label: t('back.dashboard.fans.tab') },
        { key: 'matches', label: t('back.dashboard.matches.tab') },
        { key: 'standings', label: t('back.dashboard.standings.tab') },
        { key: 'activity', label: t('back.dashboard.activity.tab') },
    ]);

    const revenueChartOptions = computed(() => {
        if (revenueChartType.value === 'ColumnChart') {
            return {
                colors: ['#3B82F6', '#10B981'],
                legend: { position: 'bottom' },
                chartArea: { width: '80%', height: '70%' },
            };
        }
        return pieChartOptions;
    });

    const occupancyChartOptions = computed(() => ({
        colors: ['#10B981'],
        legend: { position: 'none' },
        vAxis: { minValue: 0, maxValue: 100, title: '%' },
        chartArea: { width: '60%', height: '70%' },
    }));

    const pieChartOptions = {
        colors: ['#3B82F6', '#10B981'],
        pieHole: 0.3,
        legend: { position: 'bottom' },
        chartArea: { width: '80%', height: '70%' },
    };

    const comparisonColumns = computed(() => [
        { key: 'season', label: t('back.dashboard.season'), align: 'left' },
        { key: 'revenue', label: t('back.dashboard.total_revenue'), align: 'right', format: formatCurrency },
        { key: 'attendance', label: t('back.dashboard.attendance_avg'), align: 'right' },
        { key: 'abonados', label: t('back.dashboard.abonados_count'), align: 'right', format: formatNumber },
        { key: 'occupancy', label: t('back.dashboard.occupancy_kpi'), align: 'right', format: v => formatPercent(v) },
    ]);

    onMounted(() => {
        fetchSeasons();
    });
</script>
